package com.tovtalk.data.repository

import com.tovtalk.data.mapper.NotificationMapper
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.dto.ActivityNotificationDto
import com.tovtalk.data.remote.dto.UnifiedNotificationItemDto
import com.tovtalk.data.remote.dto.V2PaginatedData
import com.tovtalk.data.remote.dto.V2Response
import com.tovtalk.domain.entity.Notification
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.ActivityNotificationsResult
import com.tovtalk.domain.repository.NotificationRepository
import com.tovtalk.domain.repository.UnifiedNotificationsResult
import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.serialization.json.JsonObject

/**
 * Notification Repository Implementation
 * Uses V2Response format for all API calls.
 */
class NotificationRepositoryImpl(
    private val apiClient: ApiClient
) : NotificationRepository {
    private val notificationMapper = NotificationMapper(apiClient.bandwidthEstimator)

    override suspend fun getActivityNotifications(limit: Int, lastId: Int?): DomainResult<ActivityNotificationsResult> = try {
        val response = apiClient.httpClient.get(ApiEndpoints.ACTIVITY_NOTIFICATIONS) {
            parameter("limit", limit)
            lastId?.let { parameter("cursor", it.toString()) }
        }.body<V2Response<V2PaginatedData<ActivityNotificationDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }

        val data = response.data
        if (data != null) {
            val items = data.items.map { notificationMapper.mapActivityToEntity(it) }
            val newLastId = data.nextCursor?.toIntOrNull()
            DomainResult.Success(ActivityNotificationsResult(items = items, hasMore = data.hasMore, lastId = newLastId))
        } else {
            DomainResult.Error("No data in response")
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun getUnifiedNotifications(limit: Int, lastId: Int?, lastCreatedAt: String?): DomainResult<UnifiedNotificationsResult> = try {
        val response = apiClient.httpClient.get(ApiEndpoints.NOTIFICATIONS_LIST) {
            parameter("limit", limit)
            lastCreatedAt?.let { parameter("cursor", it) }
        }.body<V2Response<V2PaginatedData<UnifiedNotificationItemDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            return DomainResult.Error(errors.first().message)
        }

        val data = response.data
        if (data != null) {
            val items = data.items.map { notificationMapper.mapUnifiedToEntity(it) }
            DomainResult.Success(UnifiedNotificationsResult(
                items = items,
                hasMore = data.hasMore,
                lastId = items.lastOrNull()?.id,
                lastCreatedAt = data.nextCursor
            ))
        } else {
            DomainResult.Error("No data in response")
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun markNotificationAsRead(notificationId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post("${ApiEndpoints.MARK_READ}$notificationId/read")
            .body<V2Response<JsonObject>>()
        if (response.errors?.isNotEmpty() == true) {
            DomainResult.Error(response.errors.first().message)
        } else {
            DomainResult.Success(Unit)
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun markAdminNotificationAsRead(receivedId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post("${ApiEndpoints.MARK_ADMIN_READ}$receivedId/read")
            .body<V2Response<JsonObject>>()
        if (response.errors?.isNotEmpty() == true) {
            DomainResult.Error(response.errors.first().message)
        } else {
            DomainResult.Success(Unit)
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun markAllAsRead(): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post(ApiEndpoints.MARK_ALL_READ)
            .body<V2Response<JsonObject>>()
        if (response.errors?.isNotEmpty() == true) {
            DomainResult.Error(response.errors.first().message)
        } else {
            DomainResult.Success(Unit)
        }
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun deleteNotification(notificationId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete("${ApiEndpoints.DELETE_ACTIVITY_NOTIFICATION}$notificationId")
            .body<V2Response<JsonObject>>()
        if (response.errors?.isNotEmpty() == true) {
            DomainResult.Error(response.errors.first().message)
        } else {
            DomainResult.Success(Unit)
        }
    } catch (e: Exception) {
        DomainResult.Error("Failed to delete notification: ${e.message}", e)
    }

}
